import React, {
  useCallback,
  useMemo,
  useState, useEffect
} from "react";
import { Get } from "../../config/api/get";
import { RIDE } from "../../config/constants/api";
import { useSelector } from "react-redux";
import {
  GoogleMap,
  useJsApiLoader, DirectionsRenderer
} from "@react-google-maps/api";
import OngoingRideComponent from "../../components/RideComponents/OngoingRideComponent";
import { Col, Row, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import socket from "../../config/socket/index";
import ReviewModal from "../../components/ReviewModal/index";

const containerStyle = {
  width: "100%",
  height: "100%",
};

export default function OngoingRide() {
  const [message, setMessage] = useState("");
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [map, setMap] = useState(null);
  const [status, setRideStatus] = useState("");
  const [dataFetched, setDataFetched] = useState(false);
  const navigate = useNavigate();
  const [hoveredLocation, setHoveredLocation] = useState("");
  const token = useSelector((state) => state.user.userToken);
  const [pickupCoordinates, setPickupCoordinates] = useState({
    lat: 0,
    lng: 0,
  });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const handleOkReviewModal = () => {
    setShowReviewModal(false);
    navigate("/ride");
  };
  const handleDirectionsMouseOver = (locationAddress) => {
    setHoveredLocation(locationAddress);
    setModalVisible(true);
  };

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  useEffect(() => {
    socket.on("statusChanged", (data) => {
      const { status } = data;
      setRideStatus(status);
    });
    return () => {
      socket.off("statusChanged");
    };
  }, []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const getUserOngoingRide = () => {
    setLoading(true);
    Get(RIDE.getUserOngoingRide, token)
      .then((response) => {
        if (response?.status) {
          if (response?.data) {
            setData(response?.data);
            setRideStatus(response?.data.status);
          }
        } 
        else {
          navigate("/ride");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching ride", error);
      });
  };

  useEffect(() => {
    getUserOngoingRide();
  }, []);

  useEffect(() => {
    if (status === "ACCEPTED" || status === "CONFIRMED") {
      setMessage("Your Driver is Coming");
    } else if (status === "ARRIVED") {
      setMessage("Your driver has arrived");
    } else if (status === "STARTED") {
      setMessage("Your ride has started");
    } else if (status === "ENDED") {
      setMessage("Your ride has ended");
      setShowReviewModal(true);
    }
  }, [status]);

  const initialPickupCoordinates = useMemo(
    () => ({
      lat: data?.driver?.currentLocation?.coordinates[1] || 0,
      lng: data?.driver?.currentLocation?.coordinates[0] || 0,
    }),
    [data]
  );
  useEffect(() => {
    if (initialPickupCoordinates.lat && initialPickupCoordinates.lng) {
      setPickupCoordinates(initialPickupCoordinates);
    }
  }, [initialPickupCoordinates]);

  useEffect(() => {
    socket.on("updatedDriverLocation", (data) => {
      const { coordinates } = data;
      // Update pickupCoordinates with the real-time driver location
      setPickupCoordinates({
        lat: coordinates[1],
        lng: coordinates[0],
      });
    });

    // Clean up the socket listener on component unmount
    return () => {
      socket.off("updatedDriverLocation");
    };
  }, []);
  const dropoffCoordinates = useMemo(
    () => ({
      lat: data?.dropoffLocation?.coordinates[1],
      lng: data?.dropoffLocation?.coordinates[0],
    }),
    [data]
  );
  useEffect(() => {
    if (
      pickupCoordinates.lat !== 0 &&
      pickupCoordinates.lng !== 0 &&
      dropoffCoordinates.lng !== 0 &&
      dropoffCoordinates.lat !== 0 &&
      !dataFetched
    ) {
      const fetchDirectionsAndDistance = async () => {
        try {
          // Ensure DirectionsService and DistanceMatrixService are initialized
          if (window.google && window.google.maps) {
            const directionsService =
              new window.google.maps.DirectionsService();
            // Fetch Directions
            directionsService.route(
              {
                origin: pickupCoordinates,
                destination: dropoffCoordinates,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  setDirectionsResponse(result);
                } else {
                  console.error(`Error fetching directions: ${status}`);
                  message.error(
                    `Error fetching directions: ${status}. Please try again.`
                  );
                }
              }
            );
          } else {
            console.error("Google Maps API is not loaded.");
            message.error(
              "Google Maps API is not loaded. Please try again later."
            );
          }
        } catch (error) {
          console.error("Error fetching directions matrix:", error);
          message.error("Error fetching data. Please try again.");
        }
      };

      fetchDirectionsAndDistance();
    }
  }, [pickupCoordinates, dropoffCoordinates, dataFetched]);

  const onLoad = useCallback(
    (map) => {
      const bounds = new window.google.maps.LatLngBounds();
      if (pickupCoordinates) bounds.extend(pickupCoordinates);
      if (dropoffCoordinates) bounds.extend(dropoffCoordinates);
      map.fitBounds(bounds);

      setMap(map);
    },
    [pickupCoordinates, dropoffCoordinates]
  );
  const loaderOptions = useMemo(
    () => ({
      id: "google-map-script",
      googleMapsApiKey: "AIzaSyBXqV9bSEkfm5Wh7OQMj37V-n3F4AiyE40",
      libraries: ["places"],
    }),
    []
  );
  const { isLoaded } = useJsApiLoader(loaderOptions);

  return (
    <>
      {!loading ? (
        <Row justify="center py-16">
          {data ? (
            <Col md={22} xs={22}>
              <Row justify="center" gutter={[16, 16]}>
                <Col lg={6} xs={24}>
                  <OngoingRideComponent
                    data={data}
                    status={status}
                    message={message}
                  />
                </Col>
                <Col lg={18} xs={24} className="h-screen max-md:h-[550px]">
                  {data &&
                  isLoaded &&
                  pickupCoordinates &&
                  dropoffCoordinates ? (
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={pickupCoordinates}
                      zoom={15}
                      onLoad={onLoad}
                      onUnmount={onUnmount}
                    >
                      {/* {pickupCoordinates && (
                      <MarkerF
                        // icon={ImageUrl("car-icon.png")}
                        position={pickupCoordinates}
                        onMouseOver={() =>
                          handleDirectionsMouseOver(
                            data?.driver?.currentLocation?.address
                          )
                        }
                      />
                    )}
                    {dropoffCoordinates && (
                      <MarkerF
                        position={dropoffCoordinates}
                        onMouseOver={() =>
                          handleDirectionsMouseOver(
                            data?.dropoffLocation?.address
                          )
                        }
                      />
                    )} */}

                      {directionsResponse && (
                        <DirectionsRenderer
                          directions={directionsResponse}
                          options={{
                            polylineOptions: {
                              strokeColor: "blue",
                              strokeOpacity: 0.8,
                              strokeWeight: 5,
                            },
                          }}
                        />
                      )}
                    </GoogleMap>
                  ) : (
                    <Spin size="large" />
                  )}
                </Col>
              </Row>
              <ReviewModal
                open={showReviewModal}
                handleOk={handleOkReviewModal}
                handleCancel={handleOkReviewModal}
                firstName={data?.driver?.firstName}
                lastName={data?.driver?.lastName}
                driver={data?.driver?._id}
                // text="How was Your Experience"
              />
            </Col>
          ) : (
            <p>No Ongoing Ride to show</p>
          )}
        </Row>
      ) : (
        <Spin />
      )}
    </>
  );
}
